import { useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { compressImageToFile } from '@/lib/imageCompression';

/** Private bucket for guests' ID-document photos and selfies. */
export const GUEST_ID_BUCKET = 'guest-ids';

/**
 * Hook za upload fotografije dokumenta i selfieja u guest verification flowu.
 * Privatni 'guest-ids' bucket: gost smije uploadati samo pod svoju rezervaciju
 * (x-guest-token), čitati može samo iznajmljivač (signed URL). Vraća putanju
 * u bucketu ({bookingId}/{document|selfie}/{ts}-{rand}.jpg), ne javni URL.
 */
export function useGuestPhotoUpload() {
  const [isUploading, setIsUploading] = useState(false);
  const [isCompressing, setIsCompressing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const uploadPhoto = useCallback(
    async (
      file: File,
      bookingId: string,
      type: 'document' | 'selfie'
    ): Promise<string | null> => {
      setIsUploading(true);
      setError(null);

      try {
        // Validate file type
        if (!file.type.startsWith('image/')) {
          throw new Error('File must be an image');
        }

        // Max 10MB before compression
        if (file.size > 10 * 1024 * 1024) {
          throw new Error('Image must be less than 10MB');
        }

        // Compress image
        setIsCompressing(true);
        const preset =
          type === 'document'
            ? { maxWidth: 1200, maxHeight: 1600, quality: 0.85, outputFormat: 'jpeg' as const }
            : { maxWidth: 800, maxHeight: 800, quality: 0.8, outputFormat: 'jpeg' as const };

        const { file: compressedFile } = await compressImageToFile(file, preset);
        setIsCompressing(false);

        // Generate unique filename
        const timestamp = Date.now();
        const random = Math.random().toString(36).slice(2, 8);
        const filename = `${bookingId}/${type}/${timestamp}-${random}.jpg`;

        // Upload to the private guest-ids bucket (ID documents are never public)
        const { error: uploadError } = await supabase.storage
          .from(GUEST_ID_BUCKET)
          .upload(filename, compressedFile, {
            cacheControl: '3600',
            upsert: false,
            contentType: 'image/jpeg',
          });

        if (uploadError) throw uploadError;

        return filename;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Upload failed';
        setError(message);
        console.error('Guest photo upload error:', err);
        return null;
      } finally {
        setIsUploading(false);
        setIsCompressing(false);
      }
    },
    []
  );

  const uploadDocumentPhoto = useCallback(
    (file: File, bookingId: string) => uploadPhoto(file, bookingId, 'document'),
    [uploadPhoto]
  );

  const uploadSelfie = useCallback(
    (file: File, bookingId: string) => uploadPhoto(file, bookingId, 'selfie'),
    [uploadPhoto]
  );

  return {
    uploadDocumentPhoto,
    uploadSelfie,
    isUploading,
    isCompressing,
    error,
  };
}
